import earcut from 'earcut';
import {
  BufferAttribute,
  BufferGeometry,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  Object3D,
  Vector3,
} from 'three';

import { Dxf } from '../../elements/dxf';

/**
 *  The class extracts boundary geometry from a dxf-file and serves the result in a Mesh object.
 */
export class CadVolumeBoundaryDxf extends Object3D {
  private vertices: number[];
  private indices: number[];

  constructor(
    private dxf: Dxf,
    private displayGeometry: boolean = false,
  ) {
    super();

    this.vertices = [];
    this.indices = [];
  }

  /**
   *  Extracts the boundary geometry from the Dxf asset.
   */
  extractBoundaryGeometry(): Mesh | undefined {
    this.extractDxfBoundaryGeometry(this.dxf);

    return this.buildBoundaryMesh();
  }

  /**
   * Iterates the dxf structure and extract any available boundary geometry.
   * The extracted geometry will always be in local coordinates.
   *
   * @param object - The dxf element to extract boundary geometry from.
   */
  private extractDxfBoundaryGeometry(dxfElement: Dxf) {
    const nodes = dxfElement.getDxfNodes();
    for (const child of nodes) {
      switch (child.name) {
        case 'Surface':
          // TODO: Support for 3D might come at a later date, if requested.
          break;
        case 'LineCollection':
          if (this.displayGeometry) {
            this.displayLineGeometry(child as LineSegments);
          }

          this.createPolygonFromLineSegment(
            child as LineSegments,
            child.getWorldPosition(new Vector3(0, 0, 0)).clone(),
          );
          break;
        default:
          if (child instanceof LineSegments) {
            if (this.displayGeometry) {
              this.displayLineGeometry(child as LineSegments);
            }
            this.createPolygonFromLineSegment(
              child as LineSegments,
              child.getWorldPosition(new Vector3(0, 0, 0)).clone(),
            );
          }
      }
    }
  }

  /**
   *  Create a boundary polygon based on a lie segment strip.
   *  Utilizes earcut that is the basis for the geometry area polygon.
   *
   * @param lineSegment - The line segment sources.
   * @param offset - The offset to apply to the line segment boundary data.
   */
  private createPolygonFromLineSegment(lineSegment: LineSegments, offset: Vector3 = new Vector3()) {
    // A rough fail safe to ensure data farther than 5 metric miles are ignored.
    if (offset.length() > 50000) {
      return;
    }

    const vertexPositions: number[] = [];
    const linePositionArray = lineSegment.geometry.getAttribute('position') as BufferAttribute;
    for (let i = 0; i < linePositionArray.count; i++) {
      const localPosition = lineSegment.localToWorld(
        new Vector3(
          linePositionArray.getX(i),
          linePositionArray.getY(i),
          linePositionArray.getZ(i),
        ),
      );

      vertexPositions.push(localPosition.x, localPosition.y, localPosition.z);
    }

    this.appendGeometry(vertexPositions, earcut(vertexPositions, undefined, 3));
  }

  /**
   *  Appends geometry data to existing boundary geometry previously identified in the dxf structure.
   *
   * @param vertices - Vertices of the geometry.
   * @param indices - Indices of the geometry.
   */
  private appendGeometry(vertices: number[], indices: number[]) {
    const vertexCount = this.vertices.length / 3;
    this.vertices = this.vertices.concat(vertices);

    for (const index of indices) {
      this.indices.push(index + vertexCount);
    }
  }

  /**
   *  Returns a generated mesh based on the complete geometry data extracted from the dxf structure.
   */
  private buildBoundaryMesh() {
    // If there are not geometry data available, return undefined.
    if (this.vertices.length === 0 || this.indices.length === 0) {
      return undefined;
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(this.vertices), 3));
    geometry.setIndex(new BufferAttribute(new Uint16Array(this.indices), 3));

    return new Mesh(geometry);
  }

  /**
   *  Adds line segments for display in the viewer.
   *
   * @param lines . The line segment to be displayed in the viewer.
   */
  private displayLineGeometry(lines: LineSegments) {
    // Create a new instance of the line segment to utilize absolute positioning
    // as the hierarchy does not exist when only extracting some elements from the dxf.
    const newLines = new LineSegments(
      lines.geometry.clone(),
      new LineBasicMaterial({
        color: 0xdeff90,
        depthTest: false,
      }),
    );
    newLines.position.copy(lines.getWorldPosition(new Vector3()));
    this.add(newLines);
  }
}
